/** Immutable attributed history; merchant statements are labelled separately from observations. */
import { useState } from 'react'
import type { KnowledgeEntry, KnowledgeAnswerRecord } from '@testfeed/integration-contracts'
import { storeKnowledgeCopy as copy } from '@testfeed/product-language'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import { useStoreKnowledge } from '../../hooks/useStoreKnowledge'
import { knowledgeItemLabel } from './KnowledgeItemPicker'
import { trackShopifyAction, trackShopifyRefetch } from '../../shopifyUiTracking'
function AnswerRecord({ answer }: { answer: KnowledgeAnswerRecord }) {
  const response = answer.response
  let use = copy.responses[response.kind as keyof typeof copy.responses] as string
  if (response.kind === 'answer') use = copy.roles[response.role]
  let targets = null
  if (response.kind === 'answer' && response.role === 'sample') {
    let description = copy.noneTargets as string
    if (response.targetState === 'unknown') description = copy.unknownTargets
    if (response.targetState === 'selected') description = answer.targetSnapshots.map(knowledgeItemLabel).join(', ')
    targets = <s-paragraph>{description}</s-paragraph>
  }
  let context = copy.scopes[answer.scope.kind] as string
  if (answer.scope.kind === 'order_interval') context = `${context}: ${answer.scope.start} – ${answer.scope.end}`
  if (answer.scope.kind === 'unresolved') context = `${context}: ${answer.scope.description}`
  let note = null
  if (response.note) note = <s-paragraph>{response.note}</s-paragraph>
  return (
    <s-section heading={copy.version(answer.version)}>
      <s-stack gap="small">
        {answer.questionSnapshot.map((q) => (
          <s-paragraph key={q.key}>{q.text}</s-paragraph>
        ))}
        <s-text>{use}</s-text>
        {targets}
        <s-paragraph>{context}</s-paragraph>
        {note}
        <s-text color="subdued">{copy.attribution(answer.actor.id, answer.recordedAt)}</s-text>
      </s-stack>
    </s-section>
  )
}
export function KnowledgeAnswerHistory({ shopify, entry }: { shopify: ShopifyTokenSource; entry: KnowledgeEntry }) {
  const [beforeVersion, setBeforeVersion] = useState<number | undefined>()
  const query = useStoreKnowledge(shopify, { questionId: entry.questionId, beforeVersion, subject: entry.subject })
  let current = entry
  if (beforeVersion && query.data?.entries[0]) current = query.data.entries[0]
  let older = null
  if (current.answersBefore)
    older = (
      <s-button
        disabled={query.isFetching}
        onClick={() => trackShopifyAction(shopify, 'select-page', () => setBeforeVersion(current.answersBefore!))}
      >
        {copy.older}
      </s-button>
    )
  let error = null
  if (query.error)
    error = (
      <s-banner tone="critical">
        {copy.failed}
        <s-button onClick={() => void trackShopifyRefetch(shopify, () => query.refetch())}>{copy.retry}</s-button>
      </s-banner>
    )
  return (
    <s-section heading={copy.history}>
      <s-stack gap="base">
        {error}
        {current.answers.map((answer) => (
          <AnswerRecord key={answer.id} answer={answer} />
        ))}
        {older}
      </s-stack>
    </s-section>
  )
}
